import type { AxiosInstance, AxiosResponse } from 'axios';
import { validateApiResponse, logValidationResults } from '../utils/responseValidator';
import { transformApiResponse, transformListResponse } from '../utils/responseTransformer';
import { 
  isUser, 
  isStudent, 
  isActivity, 
  isDailyReport,
  isPagination,
  validateApiResponse as typeGuardValidate 
} from '../utils/typeGuards';

// Configuration for response handling
export interface InterceptorConfig {
  enableValidation: boolean;
  enableTransformation: boolean;
  enableLogging: boolean;
  strictMode: boolean;
}

const defaultConfig: InterceptorConfig = {
  enableValidation: process.env.NODE_ENV === 'development',
  enableTransformation: true,
  enableLogging: process.env.NODE_ENV === 'development',
  strictMode: false,
};

// Map of resource types to their type guards
const typeGuardMap: Record<string, (value: unknown) => boolean> = {
  'user': isUser,
  'users': (v: unknown) => Array.isArray(v) && v.every(isUser),
  'student': isStudent,
  'students': (v: unknown) => Array.isArray(v) && v.every(isStudent),
  'activity': isActivity,
  'activities': (v: unknown) => Array.isArray(v) && v.every(isActivity),
  'daily_report': isDailyReport,
  'daily_reports': (v: unknown) => Array.isArray(v) && v.every(isDailyReport),
  'pagination': isPagination,
};

// Extract resource type from URL
function extractResourceType(url: string): string | null {
  // Match patterns like /api/v1/students, /api/v1/students/123
  const patterns = [
    /\/api\/v\d+\/(\w+)(?:\/|$)/,
    /\/(\w+)(?:\/[\w-]+)?$/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) {
      return match[1].toLowerCase();
    }
  }

  return null;
}

// Determine if response contains a list
function isListResponse(url: string, data: any): boolean {
  // Check URL patterns
  if (url.match(/\/api\/v\d+\/\w+\/?$/) && !url.includes('/:id')) {
    return true;
  }

  // Check data structure
  if (data?.data && Array.isArray(data.data)) {
    return true;
  }

  return false;
}

// Setup response interceptor
export function setupResponseInterceptor(
  axiosInstance: AxiosInstance,
  config: InterceptorConfig = defaultConfig
): void {
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      const { method, url } = response.config;
      const resourceType = extractResourceType(url || '');

      if (!resourceType || !config.enableValidation) {
        return response;
      }

      try {
        // 1. Validate raw response
        if (config.enableValidation) {
          const validationResult = validateApiResponse(
            response.data,
            undefined,
            resourceType
          );

          if (config.enableLogging) {
            logValidationResults(url || '', method || 'GET', validationResult);
          }

          if (config.strictMode && !validationResult.isValid) {
            console.error('API Response validation failed in strict mode:', validationResult.errors);
            throw new Error('Response validation failed');
          }
        }

        // 2. Transform response
        if (config.enableTransformation) {
          const isList = isListResponse(url || '', response.data);
          
          if (isList) {
            const transformed = transformListResponse(
              response.data,
              resourceType.replace(/s$/, ''), // Remove plural 's'
              {
                enforceUUIDs: false,
                migrateFieldNames: true,
                flattenJsonApi: true,
                addMissingFields: true,
              }
            );
            response.data = {
              ...response.data,
              ...transformed,
            };
          } else {
            response.data = transformApiResponse(
              response.data,
              resourceType,
              {
                enforceUUIDs: false,
                migrateFieldNames: true,
                flattenJsonApi: true,
                addMissingFields: true,
              }
            );
          }
        }

        // 3. Type guard validation
        if (config.enableValidation) {
          const typeGuard = typeGuardMap[resourceType];
          if (typeGuard) {
            const dataToValidate = response.data?.data || response.data;
            const validated = typeGuardValidate(dataToValidate, typeGuard as (value: unknown) => value is unknown, {
              throwOnError: config.strictMode,
            });

            if (!validated && config.enableLogging) {
              console.warn(`Type guard validation failed for ${resourceType}`);
            }
          }
        }

        return response;
      } catch (error) {
        if (config.enableLogging) {
          console.error('Response interceptor error:', error);
        }
        
        if (config.strictMode) {
          throw error;
        }
        
        return response;
      }
    },
    (error) => {
      // Log API errors in development
      if (config.enableLogging && error.response) {
        console.error(`API Error: ${error.config?.method} ${error.config?.url}`, {
          status: error.response.status,
          data: error.response.data,
        });
      }
      
      return Promise.reject(error);
    }
  );
}

// Development-only schema monitoring
export function enableSchemaMonitoring(axiosInstance: AxiosInstance): void {
  if (process.env.NODE_ENV !== 'development') return;

  // Track response schemas
  const schemaStats = new Map<string, {
    count: number;
    validCount: number;
    errors: string[];
    warnings: string[];
  }>();

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      const endpoint = `${response.config.method} ${response.config.url}`;
      const resourceType = extractResourceType(response.config.url || '');

      if (!resourceType) return response;

      // Update statistics
      if (!schemaStats.has(endpoint)) {
        schemaStats.set(endpoint, {
          count: 0,
          validCount: 0,
          errors: [],
          warnings: [],
        });
      }

      const stats = schemaStats.get(endpoint)!;
      stats.count++;

      // Validate
      const result = validateApiResponse(response.data, undefined, resourceType);
      if (result.isValid) {
        stats.validCount++;
      } else {
        stats.errors.push(...result.errors.map(e => e.message));
      }
      stats.warnings.push(...result.warnings.map(w => w.message));

      // Log summary periodically
      if (stats.count % 10 === 0) {
        console.log(`Schema validation summary for ${endpoint}:`, {
          totalRequests: stats.count,
          validRequests: stats.validCount,
          successRate: `${((stats.validCount / stats.count) * 100).toFixed(1)}%`,
          uniqueErrors: [...new Set(stats.errors)].slice(0, 5),
          uniqueWarnings: [...new Set(stats.warnings)].slice(0, 5),
        });
      }

      return response;
    },
    (error) => Promise.reject(error)
  );

  // Export stats for debugging
  (window as any).__API_SCHEMA_STATS__ = schemaStats;
}

// Export stats getter
export const getApiStats = () => {
  return (window as any).__API_SCHEMA_STATS__ || new Map();
};